import React, { useCallback, useContext, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import styles from "./CommunicationNotifyButtons.module.css";
import { COMMUNICATION_TIMELINE_ENTRY, ICommunicationEntry } from "src/stores/CommunicationStore";
import { RootStoreContext } from "src/stores/RootStore";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import { UI } from "@wwimmo/ui";
import NotificationStatusModalList from "src/components/infoCard/notificationStatus/notificationStatusModalList/NotificationStatusModalList";
import { INotificationStatus } from "src/stores/NotificationStore";

interface CommunicationTimelineEntryProps {
    entry: ICommunicationEntry;
}

const CommunicationNotifyButtonsBase: React.FC<CommunicationTimelineEntryProps> = ({ entry }) => {
    const { t } = useTranslation();
    const { assemblyStore, communicationStore, notificationStore } = useContext(RootStoreContext);
    const [isStatusNotiDisplayed, setIsStatusNotiDisplayed] = useState<boolean>(false);
    const assemblyState = assemblyStore.currentAssembly?.state;

    const onClickNotify = useCallback(
        (state: ASSEMBLY_STATE) => async (e: React.MouseEvent) => {
            e.preventDefault();
            if (assemblyStore.hasActiveCoowners) {
                communicationStore.startAssemblyEventStateSubscription(state);
                await assemblyStore.notifyAssembly(state);
            } else {
                communicationStore.setIsHasNoActiveCoonwerDisplayed(true);
            }
        },
        [assemblyStore, communicationStore]
    );

    const onClickShowNotification = useCallback(
        () => async (e: React.MouseEvent) => {
            e.preventDefault();
            setIsStatusNotiDisplayed(true);
        },
        []
    );

    const onCloseStatusNotiModal = useCallback(() => {
        setIsStatusNotiDisplayed(false);
    }, []);

    const onCloseNoActiveCoownersModal = useCallback(() => {
        communicationStore.setIsHasNoActiveCoonwerDisplayed(false);
    }, [communicationStore]);

    const notificationStatesModal = (state: COMMUNICATION_TIMELINE_ENTRY) => {
        const notificationStates: INotificationStatus[] = [];
        notificationStore.setIsNewsOrAssemblyState(1);
        notificationStore.setNotificationtype(communicationStore.mapCommunicationStateToInteger(state));

        return (
            <UI.Modal show={isStatusNotiDisplayed} onClose={onCloseStatusNotiModal} isScrollable={true} size="lg">
                <div>
                    <NotificationStatusModalList notificationStatusList={notificationStates} />
                </div>
                <div className={styles.DivModalNotiButton}>
                    <UI.Button
                        className={styles.ModalNotiButton}
                        label={t("screens.realestate.notifications.ok")}
                        onClick={onCloseStatusNotiModal}
                        variant="outline-primary"
                    />
                </div>
            </UI.Modal>
        );
    };

    const noActiveCoownerModal = () => {
        return !assemblyStore.hasActiveCoowners ? (
            <UI.Modal
                show={communicationStore.isHasNoActiveCoonwerDisplayed}
                onClose={onCloseNoActiveCoownersModal}
                isScrollable={false}
                size="lg"
                className={styles.NoActiveCoownersModal}
            >
                <div className={styles.ModalNoActiveCowwnersTitle}>
                    {t("screens.assemblies.communicationcontent.no_notify")}
                </div>
                <div className={styles.ModalNoActiveCowwnersText}>
                    {t("screens.assemblies.communicationcontent.no_active_coowners")}
                </div>
                <div className="text-center">
                    <UI.Button
                        className={styles.ModalNoActiveCoownersButton}
                        label={t("screens.realestate.notifications.ok")}
                        onClick={onCloseNoActiveCoownersModal}
                        variant="link"
                    />
                </div>
            </UI.Modal>
        ) : undefined;
    };

    return (
        <>
            {entry.state === COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED && (
                <div className={styles.ButtonsRow}>
                    <UI.Button
                        className={styles.ButtonNotify}
                        label={t("screens.assemblies.communicationcontent.notify")}
                        onClick={onClickNotify(ASSEMBLY_STATE.DATE_PUBLISHED)}
                        hidden={
                            assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED || assemblyState === ASSEMBLY_STATE.CLOSED
                        }
                        disabled={
                            assemblyState === ASSEMBLY_STATE.PREPARING || !assemblyStore.isAssemblyInsertedEventLoaded
                        }
                    ></UI.Button>
                    {!assemblyStore.isAssemblyInsertedEventLoaded && assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED ? (
                        <UI.RotatingSpinner size={20} noLogo />
                    ) : (
                        <UI.Button
                            className={styles.ButtonStateNotify}
                            label={t("screens.assemblies.communicationcontent.notify_state")}
                            onClick={onClickShowNotification()}
                            variant="outline-primary"
                            hidden={assemblyState === ASSEMBLY_STATE.PREPARING}
                            disabled={!assemblyStore.hasActiveCoowners}
                        />
                    )}
                    {notificationStatesModal(COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED)}
                    {noActiveCoownerModal()}
                </div>
            )}

            {entry.state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED && (
                <div className={styles.ButtonsRow}>
                    <UI.Button
                        className={styles.ButtonInvite}
                        label={t("screens.assemblies.communicationcontent.invite")}
                        onClick={onClickNotify(ASSEMBLY_STATE.AGENDA_PUBLISHED)}
                        disabled={
                            assemblyState === ASSEMBLY_STATE.PREPARING ||
                            assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED ||
                            !assemblyStore.isAssemblyInsertedEventLoaded
                        }
                        hidden={assemblyState === ASSEMBLY_STATE.CLOSED}
                    />
                    {!assemblyStore.isAssemblyInsertedEventLoaded &&
                    assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED ? (
                        <UI.RotatingSpinner size={20} noLogo />
                    ) : (
                        <UI.Button
                            className={styles.ButtonStateNotify}
                            label={t("screens.assemblies.communicationcontent.notify_state")}
                            onClick={onClickShowNotification()}
                            variant="outline-primary"
                            hidden={
                                assemblyState === ASSEMBLY_STATE.PREPARING ||
                                assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED
                            }
                            disabled={!assemblyStore.hasActiveCoowners}
                        />
                    )}
                    {notificationStatesModal(COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED)}
                    {noActiveCoownerModal()}
                </div>
            )}

            {entry.state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED && (
                <div className={styles.ButtonsRow}>
                    <UI.Button
                        className={styles.ButtonNotify}
                        label={t("screens.assemblies.communicationcontent.notify")}
                        onClick={onClickNotify(ASSEMBLY_STATE.PROTOCOL_PUBLISHED)}
                        disabled={
                            assemblyState === ASSEMBLY_STATE.PREPARING ||
                            assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED ||
                            assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED ||
                            !assemblyStore.isAssemblyInsertedEventLoaded
                        }
                        hidden={assemblyState === ASSEMBLY_STATE.CLOSED}
                    />
                    {!assemblyStore.isAssemblyInsertedEventLoaded &&
                    assemblyState === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ? (
                        <UI.RotatingSpinner size={20} noLogo />
                    ) : (
                        <UI.Button
                            className={styles.ButtonStateNotify}
                            label={t("screens.assemblies.communicationcontent.notify_state")}
                            onClick={onClickShowNotification()}
                            variant="outline-primary"
                            hidden={
                                assemblyState === ASSEMBLY_STATE.PREPARING ||
                                assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED ||
                                assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED
                            }
                            disabled={!assemblyStore.hasActiveCoowners}
                        />
                    )}
                    {notificationStatesModal(COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED)}
                    {noActiveCoownerModal()}
                </div>
            )}
        </>
    );
};

export const CommunicationNotifyButtons = Sentry.withProfiler(observer(CommunicationNotifyButtonsBase));
